import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { ModuleName } from '@utils/constants';
import useModules from '@utils/hooks/useModules';
import type { BranchToken } from '@interfaces/general';

export type { BranchToken };

interface UseCanSwitchBranchResult {
  canSwitch: boolean;
  branches: BranchToken[];
  activeBranchName: string;
  hasMultiple: boolean;
  hasPermission: boolean;
  // isRoot: el dueño puede ver el CTA "Crear sucursal" (solicitud a ventas) aunque no
  // pueda cambiar de sucursal todavía. Desacoplado de canSwitch.
  isRoot: boolean;
  switchTo: (branchName: string) => void;
}

export const useCanSwitchBranch = (): UseCanSwitchBranchResult => {
  const { isRoot, refreshLogin } = useAuth();
  const { hasModule } = useModules();
  const { accountCommerceTokens } = useSubscription();
  const { commerce } = useGetCommerce();

  const branches: BranchToken[] = accountCommerceTokens ?? [];
  const activeBranchName = commerce.nombre ?? '';
  const hasMultiple = branches.length >= 2;
  // Regla de negocio multisucursal: el usuario debe ser root Y el comercio del request
  // debe tener habilitado el módulo PEDIDOS_MULTISUCURSAL (conjunción, no alternativas).
  // El backend (account-detail) ya aplica el mismo gate sobre accountCommerceTokens (L1).
  const hasPermission = isRoot && hasModule(ModuleName.PEDIDOS_MULTISUCURSAL) === true;
  const canSwitch = hasMultiple && hasPermission;

  const switchTo = (branchName: string) => {
    const target = branches.find((t) => t.nombre === branchName);
    if (target) {
      refreshLogin(target.token, target.id_comercio, {
        nombre: target.nombre,
        logotipo: target.logotipo,
        id_path: target.id_path,
      });
    }
  };

  return {
    canSwitch,
    branches,
    activeBranchName,
    hasMultiple,
    hasPermission,
    isRoot,
    switchTo,
  };
};

export default useCanSwitchBranch;
